import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Pagination,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import DirectionsRunOutlinedIcon from "@mui/icons-material/DirectionsRunOutlined";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { LojaPageContainer } from "layouts/pages/loja/common";
import { debouce, numbersOnly } from "helpers/utils";
import { getAllAtleta, searchAtleta } from "services/atleta";
import { IAtleta } from "interfaces/atleta";
import TableAtleta from "./components/TableAtleta";
import { ATLETAS_ACCENT, atletasToolbarSx } from "./atletas.theme";

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color={accent ? ATLETAS_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

const PAGE_SIZE_OPTIONS = [10, 50, 100] as const;

export default function Atletas() {
  const [isloading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [allAtletas, setAllAtletas] = useState<IAtleta[]>([]);
  const [pageCurrent, setPageCurrent] = useState(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecord, setTotalRecord] = useState(0);
  const [hasUpdated, setHasUpdated] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);

  const loadPaginated = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, status } = await getAllAtleta(pageCurrent, limit);
      if (status === 200) {
        setAllAtletas(data.results);
        setTotalPages(data.totalPages);
        setTotalRecord(data.totalRecord);
        setIsSearchMode(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [pageCurrent, limit]);

  const runSearch = useCallback(async (term: string) => {
    const q = term.trim();

    if (!q) {
      setIsSearchMode(false);
      setPageCurrent(1);
      await loadPaginated();
      return;
    }

    const cpfDigits = numbersOnly(q);
    const isCpfLikeQuery = cpfDigits.length >= 3 && /^[\d.\-\s]+$/.test(q);
    const minLength = 3;

    if (!isCpfLikeQuery && q.length < minLength) {
      return;
    }

    setIsLoading(true);
    try {
      const { data, status } = await searchAtleta(q);
      if (status === 200) {
        setAllAtletas(data);
        setTotalPages(1);
        setTotalRecord(data.length);
        setPageCurrent(1);
        setIsSearchMode(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [loadPaginated]);

  const handlehasUpdated = useCallback(async () => {
    setHasUpdated(Date.now());
    if (isSearchMode && search.trim().length >= 3) {
      await runSearch(search);
    }
  }, [isSearchMode, search, runSearch]);

  const debouncedSearch = useMemo(() => debouce(runSearch, 400), [runSearch]);

  useEffect(() => {
    if (isSearchMode) return;
    void loadPaginated();
  }, [pageCurrent, limit, hasUpdated, loadPaginated, isSearchMode]);

  const stats = useMemo(() => {
    const emailVerificados = allAtletas.filter((a) => a.user?.emailVerified).length;
    const comResetSenha = allAtletas.filter((a) => a.user?.passwordResetToken).length;
    return {
      total: totalRecord,
      exibindo: allAtletas.length,
      emailVerificados,
      comResetSenha,
    };
  }, [allAtletas, totalRecord]);

  return (
    <LojaPageContainer
      title="Atletas"
      subtitle="Cadastro de atletas, contato e verificação de e-mail."
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatMini
            label={isSearchMode ? "Resultados da busca" : "Total cadastrados"}
            value={isSearchMode ? stats.exibindo : stats.total}
            accent
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Exibindo agora" value={stats.exibindo} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="E-mail verificado (página)" value={stats.emailVerificados} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Reset de senha (página)" value={stats.comResetSenha} />
        </Grid>
      </Grid>

      <MDBox sx={atletasToolbarSx}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <DirectionsRunOutlinedIcon sx={{ color: ATLETAS_ACCENT }} />
            <MDTypography variant="button" fontWeight="medium" color="text">
              {isSearchMode
                ? `${stats.exibindo} atleta${stats.exibindo === 1 ? "" : "s"} encontrado${stats.exibindo === 1 ? "" : "s"}`
                : `${stats.exibindo} atleta${stats.exibindo === 1 ? "" : "s"} nesta página`}
            </MDTypography>
          </Stack>

          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", lg: "center" }}
            justifyContent="space-between"
          >
            <FormControl variant="outlined" size="small" sx={{ minWidth: { lg: 320 }, flex: 1 }}>
              <InputLabel htmlFor="atletas-search">Pesquisar atleta</InputLabel>
              <OutlinedInput
                id="atletas-search"
                type="text"
                value={search}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearch(value);
                  if (!value.trim()) {
                    void runSearch("");
                    return;
                  }
                  debouncedSearch(value);
                }}
                label="Pesquisar atleta"
                placeholder="Nome, CPF ou e-mail (mín. 3 caracteres)"
                endAdornment={
                  <InputAdornment position="end">
                    <Search fontSize="small" />
                  </InputAdornment>
                }
                sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
              />
            </FormControl>

            {!isSearchMode ? (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={limit}
                onChange={(_, next: number | null) => {
                  if (next == null) return;
                  setLimit(next);
                  setPageCurrent(1);
                }}
                sx={{
                  flexShrink: 0,
                  "& .MuiToggleButton-root": {
                    px: 1.25,
                    py: 0.75,
                    textTransform: "none",
                    fontSize: "0.8125rem",
                    "&.Mui-selected": {
                      bgcolor: "rgba(249, 96, 48, 0.1)",
                      borderColor: ATLETAS_ACCENT,
                      color: ATLETAS_ACCENT,
                      fontWeight: 600,
                    },
                  },
                }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <ToggleButton key={n} value={n}>
                    {n} / página
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            ) : null}
          </Stack>
        </Stack>
      </MDBox>

      {!isloading && allAtletas.length === 0 ? (
        <MDBox
          py={6}
          textAlign="center"
          sx={{
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <DirectionsRunOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            Nenhum atleta encontrado
          </MDTypography>
          <MDTypography variant="body2" color="text">
            {isSearchMode
              ? "Ajuste a busca ou limpe o campo para ver a listagem completa."
              : "Não há atletas cadastrados no momento."}
          </MDTypography>
        </MDBox>
      ) : (
        <TableAtleta
          data={allAtletas}
          isLoading={isloading}
          hasUpdated={handlehasUpdated}
        />
      )}

      {!isSearchMode && totalPages > 1 ? (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          mt={2}
        >
          <MDTypography variant="body2" color="text.secondary">
            Total cadastrados: {totalRecord}
          </MDTypography>
          <Pagination
            count={totalPages}
            page={pageCurrent}
            onChange={(_, page) => setPageCurrent(page)}
            showFirstButton
            showLastButton
            size="small"
            sx={{
              "& .MuiPaginationItem-root.Mui-selected": {
                bgcolor: "rgba(249, 96, 48, 0.12)",
                color: ATLETAS_ACCENT,
                fontWeight: 600,
              },
            }}
          />
        </Stack>
      ) : null}
    </LojaPageContainer>
  );
}
